import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import { calculateTimeInSeconds } from '../../utils';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  generalInfoSummaryQuery,
  consolidatedScoresSummaryQuery,
  scoresStatsQuery,
  updateFeedback
} from './api';

import PerformanceRankingInfo from '../UI/performanceRankingInfo';
import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';
import Summary from '../UI/game/summary';
import FeedbackForm from '../UI/game/summary/feedback-form';

export default function BalancedEquationSummaryFeedback() {
  const navigate = useNavigate();

  const {
    getUser,
    getCurrentBalancedEquationGameId
  } = useAuth();

  const user = getUser();

  const gameId = getCurrentBalancedEquationGameId();

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);
  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);

  const [rank, setRank] = useState('');
  const [points, setPoints] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [averageTime, setAverageTime] = useState(0);

  const [isPerformanceRankingInfoVisible, setIsPerformanceRankingInfoVisible] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const { data: consolidatedScores } = useQuery(consolidatedScoresSummaryQuery());
  const { data: scoresStats } = useQuery(scoresStatsQuery({ gameId, attempt: currentGameSessionAttempt }));

  const gamesSessions = generalInfo?.sessions;

  const equationScores = useMemo(() => consolidatedScores?.balanced_equation || [], [consolidatedScores]);

  const currentSessionScores = useMemo(() => {
    if (!gamesSessions || !equationScores.length) return null;

    const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
    const currentGameSession = currentGameSessions.find(session => !session.completed)
      || currentGameSessions.filter(session => session.completed).reduce((max, s) => (s.id > (max?.id || 0) ? s : max), null);

    return equationScores.find(s => s.game === gameId && s.game_session === currentGameSession?.id);
  }, [gamesSessions, equationScores, gameId, user?.id]);

  const updateFeedbackMutation = useMutation({
    mutationFn: (data) => updateFeedback(data),
    onSuccess() {
      navigate('/balanced-equation/summary/final/?after-feedback');
    }
  });

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);

      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);

        setCurrentGameSessionId(completedGameSession?.id);
        setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(currentSessionScores && scoresStats) {
      const summary = currentSessionScores.session_summary;
      const totalPoints = summary.total_points_session;
      const totalAccuracy = summary.total_accuracy_session;

      const levels = currentSessionScores.levels || [];
      const avgTime = levels.length > 0
        ? (levels.reduce((sum, l) => sum + calculateTimeInSeconds(l.average_time), 0) / levels.length).toFixed(2)
        : 0;

      setPoints(totalPoints);
      setAccuracy(totalAccuracy);
      setAverageTime(avgTime);

      // Rank/badge is server-computed and exposed as session.rank_session,
      // matching /balanced-equation/badge and /balanced-equation/summary.
      setRank(scoresStats.session?.rank_session || '');
      setDataLoaded(true);
    }
  }, [currentSessionScores, scoresStats]);

  const handleBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(true);
  }

  const handleCloseBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(false);
  }

  const handleFeedbackFormSubmit = ({rateGame, whatYouLike, whatYouDontLike}) => {
    updateFeedbackMutation.mutate({
      id: currentGameSessionId,
      feedback1: rateGame,
      feedback2: whatYouLike,
      feedback3: whatYouDontLike
    });
  }


  const feedbackMessages = useMemo(() => {
    return [
      {
        name: 'Bailey',
        text: <><br/><FeedbackForm onSubmit={handleFeedbackFormSubmit} /></>
      }
    ]
  }, [currentGameSessionId]);

  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        isPerformanceRankingInfoVisible &&
          <div className="background-overlay">
            <PerformanceRankingInfo position="center" onCloseButtonClick={handleCloseBadgeInfoButtonClick} />
          </div>
      }
      {
        dataLoaded &&
          <div className="game-summary-container">
            <PopupMessages
              messages={feedbackMessages}
              buttonText="Next"
              className="small-text-padding"
            />
            <Summary
              rank={rank}
              points={points}
              lines={[
                {label: 'Accuracy', value: accuracy},
                {label: 'Average Time', value: averageTime},
              ]}
              onBadgeInfoButtonClick={handleBadgeInfoButtonClick}
            />
          </div>
      }
    </Layout>
  );
}
